import React, { useState, createContext, useContext, useCallback, useRef, useEffect } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material'
import PropTypes from 'prop-types'

const ConfirmDialogContext = createContext(null)

export const useConfirmDialog = () => useContext(ConfirmDialogContext)

export const ConfirmDialogProvider = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [dialogContent, setDialogContent] = useState('')
  const [dialogTitle, setDialogTitle] = useState('')
  const [confirmText, setConfirmText] = useState('Confirm')
  const [cancelText, setCancelText] = useState('Cancel')

  const [resolver, setResolver] = useState(null)
  const cancelButtonRef = useRef(null)

  const handleConfirm = () => {
    setOpen(false)
    if (resolver) {
      resolver(true)
      setResolver(null)
    }
  }

  const handleCancel = () => {
    setOpen(false)
    if (resolver) {
      resolver(false)
      setResolver(null)
    }
  }

  const openDialog = useCallback(({ content, title, confirmText = 'Confirm', cancelText = 'Cancel' }) => {
    setDialogContent(content)
    setDialogTitle(title)
    setConfirmText(confirmText)
    setCancelText(cancelText)
    setOpen(true)

    return new Promise((resolve) => {
      setResolver(() => resolve)
    })
  }, [])

  useEffect(() => {
    if (open && cancelButtonRef.current) {
      cancelButtonRef.current.focus()
    }
  }, [open])

  return (
    <ConfirmDialogContext.Provider value={{ openDialog }}>
      {children}
      <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth disableEnforceFocus>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          {typeof dialogContent === 'string' ? <Typography variant="body1">{dialogContent}</Typography> : dialogContent}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} variant="outlined" ref={cancelButtonRef}>
            {cancelText}
          </Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">
            {confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmDialogContext.Provider>
  )
}

ConfirmDialogProvider.propTypes = {
  children: PropTypes.node
}
